import { Route } from '@angular/router';
import { ROUTE_PATHS } from './common/utils/route-paths.const';
import {
  authGuard,
  unauthGuard,
  authDataGuard,
  otpGuard,
  nafathGuard,
  wakalaGuard,
  kycGuard,
  bankAccountGuard,
  fundAccessGuard,
  restrictedUnitsGuard,
  investmentGuard,
  investmentAccessGuard,
  investmentBlockedGuard,
  investmentSuccessGuard,
  deleteAccountPendingGuard,
} from './common/guards/index';
import { canMatchLang, languageGuard, langRedirect } from '@ui-lib';

export const appRoutes: Route[] = [
  /**
   * Deep link redirects (e.g. from marketing emails/push notifications).
   * Top-level (not ':lang' children) so the path's own ':lang' segment can
   * be carried into an absolute redirectTo. The SSR route manifest resolves
   * that via its own positional `*` wildcard, not ':paramName' syntax.
   */
  {
    path: `:lang/${ROUTE_PATHS.LINKS_PROFILE}`,
    redirectTo: `/*/${ROUTE_PATHS.ACCOUNT}`,
    pathMatch: 'full',
  },
  {
    path: `:lang/${ROUTE_PATHS.LINKS_MARKETING_MESSAGE}`,
    redirectTo: `/*/${ROUTE_PATHS.HOME}`,
    pathMatch: 'full',
  },
  {
    path: ':lang',
    canMatch: [canMatchLang],
    canActivate: [languageGuard],
    children: [
      {
        path: ROUTE_PATHS.ROOT,
        redirectTo: ROUTE_PATHS.HOME,
        pathMatch: 'full',
      },
      /**
       * Auth Routes
       * Note: unauthGuard is applied to individual child routes instead of the parent.
       * This allows success screens (reset-password-success, create-account-success)
       * to be accessible to authenticated users while still using the auth layout.
       */
      {
        path: ROUTE_PATHS.AUTH,
        loadComponent: () => import('./pages/auth/auth').then(m => m.Auth),
        children: [
          {
            path: ROUTE_PATHS.ROOT,
            redirectTo: ROUTE_PATHS.PHONE_VERIFICATION,
            pathMatch: 'full',
          },
          {
            path: ROUTE_PATHS.PHONE_VERIFICATION,
            canActivate: [unauthGuard],
            loadComponent: () =>
              import('./pages/auth/phone-verification/phone-verification').then(
                m => m.PhoneVerification
              ),
          },
          {
            path: ROUTE_PATHS.SIGN_UP,
            canActivate: [unauthGuard, authDataGuard, otpGuard],
            data: { requireInvestorType: true, requireOtpToken: true },
            loadComponent: () =>
              import('./pages/auth/sign-up/sign-up').then(m => m.SignUp),
          },
          {
            path: ROUTE_PATHS.SIGN_IN,
            canActivate: [unauthGuard, authDataGuard],
            loadComponent: () =>
              import('./pages/auth/sign-in/sign-in').then(m => m.SignIn),
          },
          {
            path: ROUTE_PATHS.FORGOT_PASSWORD,
            canActivate: [unauthGuard, otpGuard],
            data: { requireOtpToken: true },
            loadComponent: () =>
              import('./pages/auth/forgot-password/forgot-password').then(
                m => m.ForgotPassword
              ),
          },
          {
            path: `${ROUTE_PATHS.OTP_VERIFICATION}/:type`,
            canActivate: [unauthGuard, otpGuard],
            data: { requireOtpToken: false },
            loadComponent: () =>
              import('./pages/auth/otp-verification/otp-verification').then(
                m => m.OtpVerification
              ),
          },
          {
            path: ROUTE_PATHS.INVESTOR_TYPE,
            canActivate: [unauthGuard, otpGuard],
            data: { requireOtpToken: true },
            loadComponent: () =>
              import('./pages/auth/investor-type/investor-type').then(
                m => m.InvestorType
              ),
          },
          {
            path: ROUTE_PATHS.RESET_PASSWORD_SUCCESS,
            loadComponent: () =>
              import('./pages/auth/success-screens/reset-password-success/reset-password-success').then(
                m => m.ResetPasswordSuccess
              ),
          },
          {
            path: ROUTE_PATHS.CREATE_ACCOUNT_SUCCESS,
            loadComponent: () =>
              import('./pages/auth/success-screens/create-account-success/create-account-success').then(
                m => m.CreateAccountSuccess
              ),
          },
        ],
      },
      {
        path: ROUTE_PATHS.ROOT,
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/layout/layout').then(m => m.Layout),
        children: [
          {
            path: ROUTE_PATHS.HOME,
            loadComponent: () => import('./pages/home/home').then(m => m.Home),
          },
          {
            path: `${ROUTE_PATHS.FUNDS}/:id`,
            canActivate: [fundAccessGuard],
            loadComponent: () =>
              import('./pages/home/fund-details/fund-details').then(
                m => m.FundDetails
              ),
          },
          {
            path: `${ROUTE_PATHS.FUNDS}/:id/${ROUTE_PATHS.RESTRICTED_UNITS}`,
            canActivate: [restrictedUnitsGuard],
            loadComponent: () =>
              import('./pages/restricted-units/restricted-units').then(
                m => m.RestrictedUnitsComponent
              ),
          },
          {
            path: ROUTE_PATHS.MY_INVESTMENTS,
            loadComponent: () =>
              import('./pages/my-investment/my-investment').then(
                m => m.MyInvestment
              ),
          },
          {
            // Full-page counterpart, reached via the off-canvas' expand button.
            path: `${ROUTE_PATHS.MY_INVESTMENTS}/${ROUTE_PATHS.DETAILS}/:investmentUuid`,
            loadComponent: () =>
              import('./pages/my-investment/components/transaction-details/transaction-details').then(
                m => m.TransactionDetailsComponent
              ),
          },
          {
            path: ROUTE_PATHS.ACCOUNT,
            loadComponent: () =>
              import('./pages/account/account').then(m => m.Account),
            children: [
              {
                path: ROUTE_PATHS.MANAGE_ACCOUNT,
                loadComponent: () =>
                  import('./pages/account/manage-account/manage-account').then(
                    m => m.ManageAccount
                  ),
              },
              {
                path: ROUTE_PATHS.BANK_ACCOUNT,
                loadComponent: () =>
                  import('./pages/account/bank-account/bank-account').then(
                    m => m.BankAccount
                  ),
                children: [
                  {
                    path: ROUTE_PATHS.ROOT,
                    pathMatch: 'full',
                    redirectTo: ROUTE_PATHS.PREVIEW,
                  },
                  {
                    path: ROUTE_PATHS.PREVIEW,
                    loadComponent: () =>
                      import('./pages/account/bank-account/preview/preview').then(
                        m => m.BankAccountPreview
                      ),
                  },
                  {
                    path: ROUTE_PATHS.DETAILS,
                    canActivate: [bankAccountGuard],
                    loadComponent: () =>
                      import('./pages/account/bank-account/details/details').then(
                        m => m.BankAccountDetails
                      ),
                  },
                  {
                    path: ROUTE_PATHS.UPDATE,
                    canActivate: [bankAccountGuard],
                    loadComponent: () =>
                      import('./pages/account/bank-account/update/update').then(
                        m => m.BankAccountUpdate
                      ),
                  },
                ],
              },
              {
                path: ROUTE_PATHS.SETTINGS,
                loadComponent: () =>
                  import('./pages/account/settings/settings').then(
                    m => m.Settings
                  ),
              },
              {
                path: ROUTE_PATHS.HELP_SUPPORT,
                loadComponent: () =>
                  import('./pages/account/help-support/help-support').then(
                    m => m.HelpSupport
                  ),
              },
            ],
          },
          {
            path: ROUTE_PATHS.PRIVACY_SECURITY,
            loadComponent: () =>
              import('./pages/account/help-support/tabs/privacy-security/pages/pages').then(
                m => m.Pages
              ),
            children: [
              {
                path: ROUTE_PATHS.ROOT,
                redirectTo: ROUTE_PATHS.PRIVACY_POLICY,
                pathMatch: 'full',
              },
              {
                path: ROUTE_PATHS.PRIVACY_POLICY,
                loadComponent: () =>
                  import('./pages/account/help-support/tabs/privacy-security/pages/privacy-policy/privacy-policy').then(
                    m => m.PrivacyPolicy
                  ),
              },
              {
                path: ROUTE_PATHS.REQUEST_DATA_COPY,
                loadComponent: () =>
                  import('./pages/account/help-support/tabs/privacy-security/pages/request-data-copy/request-data-copy').then(
                    m => m.RequestDataCopy
                  ),
              },
              {
                path: ROUTE_PATHS.DELETE_ACCOUNT,
                loadComponent: () =>
                  import('./pages/account/help-support/tabs/privacy-security/pages/delete-account/delete-account').then(
                    m => m.DeleteAccount
                  ),
              },
            ],
          },
          /**
           * Nafath Routes
           * Parent route with router outlet for Nafath flow
           * Protected by nafathGuard - only INDIVIDUAL investors with appropriate status
           */
          {
            path: ROUTE_PATHS.NAFATH,
            loadComponent: () =>
              import('./pages/nafath/nafath').then(m => m.Nafath),
            children: [
              {
                path: ROUTE_PATHS.ROOT,
                redirectTo: ROUTE_PATHS.INITIATE,
                pathMatch: 'full',
              },
              {
                path: ROUTE_PATHS.INITIATE,
                loadComponent: () =>
                  import('./pages/nafath/initiate/initiate').then(
                    m => m.NafathInitiate
                  ),
                canActivate: [nafathGuard],
              },
              {
                path: ROUTE_PATHS.VERIFY,
                loadComponent: () =>
                  import('./pages/nafath/verify/verify').then(
                    m => m.NafathVerify
                  ),
                canActivate: [nafathGuard],
              },
              {
                path: ROUTE_PATHS.UPDATE,
                loadComponent: () =>
                  import('./pages/nafath/update/update').then(
                    m => m.NafathUpdate
                  ),
                canActivate: [nafathGuard],
              },
            ],
          },
          /**
           * Wakala Route
           * Protected by wakalaGuard - only users with completed nafath status
           */
          {
            path: ROUTE_PATHS.WAKALA,
            canActivate: [wakalaGuard],
            loadComponent: () =>
              import('./pages/wakala/wakala').then(m => m.Wakala),
          },
          /**
           * KYC Route
           * Protected by kycGuard - redirects home if KYC already pending review
           */
          {
            path: ROUTE_PATHS.KYC,
            canActivate: [kycGuard],
            loadComponent: () => import('./pages/kyc/kyc').then(m => m.Kyc),
          },
          {
            path: `${ROUTE_PATHS.INVESTMENT}/:fund`,
            canActivate: [investmentAccessGuard, investmentGuard],
            loadComponent: () =>
              import('./pages/investment/investment').then(
                m => m.InvestmentPage
              ),
            children: [
              {
                path: ROUTE_PATHS.ROOT,
                pathMatch: 'full',
                loadComponent: () =>
                  import('./pages/investment/suitability/suitability').then(
                    m => m.SuitabilityStepComponent
                  ),
              },
              {
                path: ROUTE_PATHS.INVESTMENT_PAYMENT,
                loadComponent: () =>
                  import('./pages/investment/components/payment/payment').then(
                    m => m.PaymentComponent
                  ),
              },
              {
                path: ROUTE_PATHS.INVESTMENT_PAYMENT_INSTRUCTIONS,
                loadComponent: () =>
                  import('./pages/investment/payment-instructions/payment-instructions').then(
                    m => m.PaymentInstructionsComponent
                  ),
              },
              {
                path: ROUTE_PATHS.INVESTMENT_CHECKOUT,
                loadComponent: () =>
                  import('./pages/investment/investment-checkout/investment-checkout').then(
                    m => m.InvestmentCheckoutComponent
                  ),
              },
            ],
          },
          {
            path: ROUTE_PATHS.INVESTMENT_BLOCKED,
            canActivate: [investmentBlockedGuard],
            loadComponent: () =>
              import('./pages/investment/investment-blocked/investment-blocked').then(
                m => m.InvestmentBlockedPage
              ),
          },
          {
            path: ROUTE_PATHS.INVESTMENT_SUCCESS,
            canActivate: [investmentSuccessGuard],
            loadComponent: () =>
              import('./pages/investment/investment-success/investment-success').then(
                m => m.InvestmentSuccessPage
              ),
          },
        ],
      },
      {
        path: ROUTE_PATHS.DELETE_ACCOUNT_PENDING,
        canActivate: [deleteAccountPendingGuard],
        loadComponent: () =>
          import('./pages/delete-account-pending/delete-account-pending').then(
            m => m.DeleteAccountPending
          ),
      },
      {
        path: `${ROUTE_PATHS.STATUS}/:type`,
        loadComponent: () =>
          import('./pages/status/status').then(m => m.Status),
      },
      {
        path: '**',
        redirectTo: `${ROUTE_PATHS.STATUS}/${ROUTE_PATHS.NOT_FOUND}`,
      },
    ],
  },
  {
    path: '',
    pathMatch: 'full',
    redirectTo: langRedirect,
  },
  {
    path: '**',
    redirectTo: langRedirect,
  },
];
